import useGetControlModeTemplates from '@hooks/api/queries/modes/useGetControlModeTemplates';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ControlModeTemplate } from '@src/types/ControlModes';
import React, { useCallback, useState } from 'react';
import ControlModesTemplateCreateEditModal from '../../ConfigurableModes/ControlModesTemplateCreateEditModal';
import TemplatesList from '../../ConfigurableModes/TemplatesList';
import { useStackModalContext } from '../context/StackModalContext';

const StyledDialogHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
}));

const TemplatePicker: React.FC = () => {
  const {
    state: { isTemplatePickerOpen, currentControlModeIndex },
    actions: {
      template: { handleCloseTemplatePicker, handleOpenTemplateConfirm },
    },
  } = useStackModalContext();

  // State for selected templates
  const [selectedTemplates, setSelectedTemplates] = useState<
    ControlModeTemplate[]
  >([]);
  const { isLoading } = useGetControlModeTemplates();

  // State for template creation/editing
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ControlModeTemplate | null>(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [handleSaveRef, setHandleSaveRef] = useState<
    (() => Promise<void>) | null
  >(null);

  // Handle template selection
  const handleTemplateSelection = useCallback(
    (templates: ControlModeTemplate[]) => {
      setSelectedTemplates(templates);
    },
    [],
  );

  // Handle applying the selected templates
  const handleApply = useCallback(() => {
    if (currentControlModeIndex !== null) {
      handleOpenTemplateConfirm(selectedTemplates, currentControlModeIndex);
      handleCloseTemplatePicker();
    }
  }, [
    handleOpenTemplateConfirm,
    selectedTemplates,
    currentControlModeIndex,
    handleCloseTemplatePicker,
  ]);

  const handleCreateClick = useCallback(() => {
    setSelectedTemplate(null);
    setIsCreatingTemplate(true);
    setConfigModalOpen(true);
  }, []);

  const handleEditClick = useCallback((template: ControlModeTemplate) => {
    setSelectedTemplate(template);
    setIsCreatingTemplate(false);
    setConfigModalOpen(true);
  }, []);

  const handleConfigModalClose = useCallback(() => {
    setConfigModalOpen(false);
    setHandleSaveRef(null);
    setIsCreatingTemplate(false);
  }, []);

  const handleSaveClick = useCallback(() => {
    if (handleSaveRef) {
      handleSaveRef();
    }
  }, [handleSaveRef]);

  // Reset selected templates when the dialog opens or closes
  React.useEffect(() => {
    if (!isTemplatePickerOpen) {
      setSelectedTemplates([]);
    }
  }, [isTemplatePickerOpen]);

  return (
    <>
      <Dialog
        open={isTemplatePickerOpen}
        onClose={handleCloseTemplatePicker}
        fullWidth
        maxWidth='lg'
      >
        <StyledDialogHeader>
          <Typography
            variant='h6'
            sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
          >
            Select Template
          </Typography>
          <IconButton
            onClick={handleCloseTemplatePicker}
            sx={{ mr: -2, mt: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogHeader>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='subtitle1'>
            Select a template to apply to this control mode
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant='outlined'
            onClick={handleCreateClick}
          >
            Create New Template
          </Button>
        </Box>
        <Divider />
        <DialogContent dividers>
          {isLoading ? (
            <Typography>Loading templates...</Typography>
          ) : (
            <TemplatesList
              selectionMode={true}
              singleSelection={true}
              onTemplatesSelected={handleTemplateSelection}
              onEditTemplate={handleEditClick}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplatePicker} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant='contained'
            color='primary'
            disabled={selectedTemplates.length !== 1}
          >
            Apply Selected Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Create/Edit Modal */}
      <Dialog
        open={configModalOpen}
        onClose={handleConfigModalClose}
        fullWidth
        maxWidth='md'
      >
        <StyledDialogHeader>
          <Typography
            variant='h6'
            sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
          >
            {isCreatingTemplate
              ? 'Create Template'
              : `Edit Template: ${selectedTemplate?.name}`}
          </Typography>
          <IconButton onClick={handleConfigModalClose} sx={{ mr: -2, mt: -1 }}>
            <CloseIcon />
          </IconButton>
        </StyledDialogHeader>
        <DialogContent dividers>
          {isCreatingTemplate ? (
            <ControlModesTemplateCreateEditModal
              onClose={handleConfigModalClose}
              onSave={(saveFunction: () => Promise<void>) => {
                setHandleSaveRef(() => saveFunction);
              }}
            />
          ) : (
            selectedTemplate && (
              <ControlModesTemplateCreateEditModal
                templateId={String(selectedTemplate.id)}
                templateName={selectedTemplate.name}
                onClose={handleConfigModalClose}
                onSave={(saveFunction: () => Promise<void>) => {
                  setHandleSaveRef(() => saveFunction);
                }}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='primary' onClick={handleSaveClick}>
            SAVE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Export the component as both default and named export
export { TemplatePicker };
export default TemplatePicker;
